import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listTemplates, setTemplate, type AdminTemplate } from '../../api/admin';
import { Alert, Badge, Button, DataTable, LoadingState } from '../ui';

/**
 * Which Quick Brew templates this instance offers.
 *
 * The templates themselves are code — they have to be, they build charts — so
 * what is managed here is whether each one is offered. A template turned off is
 * refused by the backend too, not merely hidden.
 */
export function AdminTemplates() {
  const [templates, setTemplates] = useState<AdminTemplate[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const answer = await listTemplates();
      setTemplates(answer.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (template: AdminTemplate) => {
    setWorking(template.key);
    setError(null);
    try {
      await setTemplate(template.key, !template.is_enabled);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  if (templates === null) {
    return <LoadingState label="Reading the templates…" />;
  }

  return (
    <>
      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      <DataTable
        caption="Quick Brew templates"
        rows={templates}
        rowKey={(template) => template.key}
        columns={[
          {
            key: 'template',
            header: 'Template',
            render: (template) => (
              <>
                <strong>{template.name}</strong>
                <p className="sc-admin__muted">{template.description}</p>
              </>
            ),
          },
          {
            key: 'state',
            header: 'State',
            render: (template) => (
              <Badge tone={template.is_enabled ? 'success' : 'neutral'}>
                {template.is_enabled ? 'Offered' : 'Not offered'}
              </Badge>
            ),
          },
          {
            key: 'toggle',
            header: 'Offer it',
            render: (template) => (
              <Button
                size="sm"
                variant="secondary"
                isLoading={working === template.key}
                onClick={() => void toggle(template)}
              >
                {template.is_enabled ? 'Stop offering' : 'Offer again'}
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}
